
import {
  defineComponent,
  watch,
  reactive,
  computed,
} from 'vue';
import { useStore } from 'vuex';
import { i18n, stringFromDate } from '@/functions';

export default defineComponent({
  props: {
    open: Boolean,
    onSelect: Function,
    onDateSelect: {
      type: Function,
      required: true,
    },
    df: Date,

    hasDisabled: { type: Boolean, default: true },
    hasSecondDate: Boolean,
    hasYear: Boolean,
    onSecondDateSelect: Function,
  },
  setup(props) {
    const $store = useStore();
    const state = reactive({
      isSettingDateFrom: true,
      currentDate: new Date(
        new Date(new Date().setDate(1)).setHours(12, 0, 0, 0),
      ),
      focusedDates: [],
      i18n,
    });
    // !!!
    // date bug
    // SOLUTION: (as unknown) as Date
    const df = computed(() => (props.df as unknown) as Date);
    const dt = computed(() => (props.hasSecondDate
      ? (new Date($store.state.initials.common.dateto) as Date)
      : null));
    const datefrom = computed(() => (df.value ? df.value?.toLocaleDateString('ru-RU') : ''));
    // const dateto = computed(() => (props.hasSecondDate && dt.value
    //   ? dt.value.toLocaleDateString('ru-RU')
    //   : ''));
    const updateDateFrom = (date: Date): void => {
      props.onDateSelect({ date });
      if (!props.hasSecondDate && props.onSelect) {
        props.onSelect();
        return;
      }
      state.isSettingDateFrom = false;
    };
    const updateDateTo = (date: Date): void => {
      if (props.onSecondDateSelect) props.onSecondDateSelect({ date });
      state.isSettingDateFrom = true;
    };

    // text getters
    const getLocaleMonth = (date: Date): string => (
      [i18n.month[date.getMonth()].slice(0, 3), date.getFullYear()]
        .join(
          ' ',
        )
    );

    // state checks
    const isActive = (day: number, date: Date): boolean => {
      const dateCopy = new Date(date);
      const dateString = stringFromDate(new Date(dateCopy.setDate(day)));
      if (dateString === (datefrom.value as string)) return true;

      if (!dt.value) return false;

      return (state.focusedDates as number[]).includes(dateCopy.setDate(day));
    };
    const isDisabled = (day: number, date: Date): boolean => (props.hasDisabled
      ? new Date(new Date(date).setDate(day)).getTime() < new Date().getTime()
      : false);
    const isFocused = (day: number, date: Date): boolean => {
      if (isDisabled(day, date)) return false;
      const dateCopy = new Date(date);
      return (state.focusedDates as number[]).includes(dateCopy.setDate(day));
    };

    // service getters;
    const nextMonth = (date: Date, index = 1): Date => {
      const dateCopy = new Date(date);
      return new Date(dateCopy.setMonth(dateCopy.getMonth() + index));
    };
    const nextDate = computed(() => nextMonth(state.currentDate as Date));
    const currentMonths = computed(() => getLocaleMonth(state.currentDate as Date));
    const nextMonths = computed(() => getLocaleMonth(nextDate.value as Date));
    // const inactiveDays = computed(() => getInactiveDays(state.currentDate as Date))
    const getInactiveDays = (date: Date): number => {
      const dateCopy = new Date(date);
      const day = new Date(dateCopy.setDate(1)).getDay() - 1;
      return day >= 0 ? day : 6;
    };
    const daysInMonth = (date: Date): number => {
      const dateCopy = new Date(date);
      return (
        33 - new Date(dateCopy.getFullYear(), dateCopy.getMonth(), 33).getDate()
      );
    };

    // year
    const isActiveYear = (year: number) => state.currentDate.getFullYear() === year;
    const currentYearString = computed(() => {
      const currentYear = state.currentDate.getFullYear();
      const currentYearStart = Math.floor(currentYear / 10) * 10;
      return `${currentYearStart} - ${currentYearStart + 10}`;
    });
    const currentYearList = computed(() => {
      const currentYear = state.currentDate.getFullYear();
      let currentYearStart = Math.floor(currentYear / 10) * 10;
      return [...Array(10)].map(() => {
        currentYearStart += 1;
        return currentYearStart;
      });
    });
    const setYear = (year: number) => {
      state.currentDate = new Date(state.currentDate.setFullYear(year));
    };

    // methods
    const setDate = (day: number, date: Date): void => {
      if (isDisabled(day, date)) return;
      const dateCopy = new Date(date);
      dateCopy.setDate(day);
      if (state.isSettingDateFrom) {
        updateDateFrom(dateCopy);
        return;
      }

      updateDateTo(dateCopy);
      if (props.onSelect) props.onSelect();
    };
    const nav = (index: number): void => {
      state.currentDate = nextMonth(state.currentDate as Date, index);
    };
    const collectFocused = (a: number, b: number = a) => {
      const from = Math.min(a, b);
      const to = Math.max(a, b);
      const focused = [];

      for (let time = from; time <= to; time += 86400000) focused.push(time);
      (state.focusedDates as number[]) = focused;
    };
    const setFocused = (day: number, date: Date) => {
      if (state.isSettingDateFrom) return;

      if (!day) state.focusedDates = [];
      collectFocused(
        (df.value as Date).getTime(),
        new Date(new Date(date).setDate(day)).getTime(),
      );
    };

    // no return
    const handleNoReturnClick = () => {
      state.isSettingDateFrom = true;
      $store.commit('updateSegment', { index: 1, segment: { date: null } });
      collectFocused(df.value.getTime());
      if (props.onSelect) props.onSelect();
    };

    // created
    const initialize = () => {
      if (!df.value) return;
      collectFocused(df.value.getTime());
    };
    initialize();

    // nav to valid month
    if (daysInMonth(state.currentDate as Date) === state.currentDate.getDate()) { nav(1); }
    watch(
      () => props.open,
      () => {
        if (!state.currentDate || !df.value) return;
        while (state.currentDate.getMonth() < df.value.getMonth()) nav(1);
        while (state.currentDate.getFullYear() < df.value.getFullYear()) { nav(12); }
        while (state.currentDate.getMonth() > df.value.getMonth()) nav(-1);
        while (state.currentDate.getFullYear() > df.value.getFullYear()) { nav(-12); }
      },
    );

    // remove focus if isComposite
    watch(
      () => props.hasSecondDate,
      () => df.value && collectFocused(df.value.getTime()),
    );
    return {
      state,
      i18n,
      nextDate,
      currentMonths,
      nextMonths,
      getInactiveDays,
      daysInMonth,
      isActive,
      isFocused,
      isDisabled,
      // isSettingDateFrom,
      setFocused,
      setDate,
      nav,
      handleNoReturnClick,

      currentYearString,
      currentYearList,
      setYear,
      isActiveYear,
    };
  },
});
